import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UilBell, UilCheck, UilTimes } from '@iconscout/react-unicons';
import { errorAlert } from '@helpers/alerts/alerts';
import { useLanguageContext } from '@hooks/useLanguageContext';
import {
  useGetReadOrDeletedNotificationsQuery,
  useDeleteNotificationMutation,
  useDeleteAllNotificationsMutation,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
} from '@api/notification/notificationAPI';
import { ButtonSizes, ButtonTypes, ButtonVariants } from '@components/Button/types';
import { Button } from 'components/Button';
import { ErrorResponse, INotifications } from './types';
import './styles.scss';

export const Notifications = () => {
  const { dictionary } = useLanguageContext();

  const authString = localStorage.getItem('persist:auth') || '';

  const auth = JSON.parse(authString);

  const user = JSON.parse(auth.auth).user;

  const navigate = useNavigate();

  const [deleteNotification] = useDeleteNotificationMutation();

  const [deleteAllNotifications] = useDeleteAllNotificationsMutation();

  const [readNotification] = useMarkNotificationAsReadMutation();

  const [readAllNotifications] = useMarkAllNotificationsAsReadMutation();

  const { data: notificationsData, refetch } = useGetReadOrDeletedNotificationsQuery(user?.id) as {
    data: { notifications: INotifications[] };
    refetch: () => void;
  };

  const handleDeleteSingle = async (appointmentId: string) => {
    try {
      await deleteNotification({
        userId: user.id,
        appointmentId,
      }).unwrap();
      await refetch();
    } catch (error: unknown) {
      const errorMessage =
        (error as ErrorResponse)?.data?.error ||
        dictionary.tooltip.notifications.errors.deleteFailed;

      errorAlert(errorMessage);
    }
  };

  const handleReadSingle = async (appointmentId: string) => {
    try {
      await readNotification({
        userId: user.id,
        appointmentId,
      }).unwrap();
      await refetch();
    } catch (error: unknown) {
      const errorMessage =
        (error as ErrorResponse)?.data?.error || dictionary.tooltip.notifications.errors.readFailed;

      errorAlert(errorMessage);
    }
  };

  const handleDeleteAll = async () => {
    try {
      const appointmentIds =
        notificationsData?.notifications?.map(({ appointment }) => appointment?.id) || [];

      await deleteAllNotifications({
        userId: user.id,
        appointmentIds,
      }).unwrap();
      await refetch();
    } catch (error: unknown) {
      const errorMessage =
        (error as ErrorResponse)?.data?.error ||
        dictionary.tooltip.notifications.errors.deleteAllFailed;

      errorAlert(errorMessage);
    }
  };

  const handleReadAll = async () => {
    try {
      const appointmentIds =
        notificationsData?.notifications?.map((n: any) => n.appointment.id) || [];

      await readAllNotifications({
        userId: user.id,
        appointmentIds,
      }).unwrap();
      await refetch();
    } catch (error: unknown) {
      const errorMessage =
        (error as ErrorResponse)?.data?.error ||
        dictionary.tooltip.notifications.errors.readAllFailed;

      errorAlert(errorMessage);
    }
  };

  return (
    <div className="notifications" role="button" tabIndex={0}>
      <span className="notifications-control">
        <span className="notifications-amount">
          {notificationsData?.notifications?.filter(({ read }: { read: boolean }) => !read)
            .length || 0}
        </span>
        <UilBell size={24} />
      </span>
      {!!notificationsData?.notifications.length && (
        <div className="notifications-tooltip">
          <h3 className="notifications-title">Notifications</h3>
          <ul className="rejected-appointments">
            {notificationsData?.notifications?.map((appointmentDetail: any, index: number) => {
              const isRead = appointmentDetail?.read;

              const handleMouseEnter = () => {
                if (!isRead) {
                  handleReadSingle(appointmentDetail?.appointment?.id);
                }
              };

              return (
                <li
                  key={`notifications${index}`}
                  className={`rejected-appointment ${isRead ? 'read' : 'unread'}`}
                  onMouseEnter={handleMouseEnter}
                >
                  <div className="container-rejected-appointment">
                    <div className="container-titels">
                      <strong
                        className="appointment-titel bold-text"
                        onClick={() =>
                          navigate(`/appointments/${appointmentDetail?.appointment.id}`)
                        }
                      >
                        {appointmentDetail?.appointment.name}
                      </strong>
                      <span> for </span>
                      <strong className="bold-text">
                        {appointmentDetail?.appointment?.trials?.protocol_name}
                      </strong>
                      <span> was rejected by </span>
                      <strong className="bold-text">
                        {appointmentDetail?.appointment?.patient_name}{' '}
                        {appointmentDetail?.appointment?.patient_surname}
                      </strong>
                    </div>
                    <Button
                      type={ButtonTypes.submit}
                      iconPrepend={<UilTimes />}
                      variant={ButtonVariants.link}
                      handleClick={() => handleDeleteSingle(appointmentDetail.appointment.id)}
                      size={ButtonSizes.small}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="container-buttons">
            <Button
              label={dictionary.tooltip.notifications.button.label.allread}
              iconPrepend={<UilCheck size={24} />}
              variant={ButtonVariants.warning}
              handleClick={handleReadAll}
              size="static"
            />
            <Button
              label={dictionary.tooltip.notifications.button.label.clearall}
              iconPrepend={<UilTimes size={24} />}
              variant={ButtonVariants.danger}
              handleClick={handleDeleteAll}
              size="static"
            />
          </div>
        </div>
      )}
    </div>
  );
};
